import type { AudioTrack, SubtitleStyleOptions } from './language-menu.types';
import type { MenuOptions } from '../menu/menu-wrapper';
import type { SubtitleOption } from '../settings-menu/settings-menu.types';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLanguageMenuItems } from './hooks/use-language-menu-items';
import { MenuWrapper } from '../menu/menu-wrapper';
import { COLORS } from '../user-interfaces/hooks/use-player-colors-styles';

type Props = {
  isOpen?: boolean;
  chosenSubtitleId?: string;
  chosenAudioTrackId?: string;
  subtitles: SubtitleOption[];
  audioTracks: AudioTrack[];
  subtitlesEnabled: boolean;
  onSetSubtitle?: (subtitleId: string) => void;
  onSetAudioTrack?: (audioTrackId: string) => void;
  subtitleStyleOptions?: SubtitleStyleOptions;
  setSubtitleStyleOptions?: (options: SubtitleStyleOptions) => void;
  onClose: () => void;
  resetSubtitleStyleOptions?: () => void;
};

const AUDIO_MENU_ID = 'audio-menu';
const SUBTITLES_MENU_ID = 'subtitles-menu';
const LANGUAGE_MENU_ID = 'language-menu';

export function LanguageMenu({
  isOpen,
  chosenSubtitleId,
  chosenAudioTrackId,
  subtitles = [],
  audioTracks = [],
  subtitlesEnabled,
  onSetSubtitle,
  onSetAudioTrack,
  subtitleStyleOptions,
  setSubtitleStyleOptions,
  resetSubtitleStyleOptions,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const { menuItems } = useLanguageMenuItems(audioTracks, subtitles, chosenSubtitleId);

  const menuOptions: MenuOptions = useMemo(() => {
    const audioMenuOptions = menuItems.filter((item) => item.type === 'audio');
    const subtitlesMenuOptions = menuItems.filter((item) => item.type === 'subtitle');

    const showAudioSubmenu = !!audioTracks?.length;
    const showSubtitlesSubmenu = !!subtitles?.length;

    const colorOptions = Object.keys(COLORS).map<MenuOptions>((color) => ({
      id: COLORS[color as keyof typeof COLORS],
      label: (
        <>
          {t(`colors.${color}`)}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle
              cx="8"
              cy="8"
              r="8"
              fill={color === 'white' || color === 'black' ? 'var(--textColor)' : COLORS[color as keyof typeof COLORS]}
            />
            <circle cx="8" cy="8" r="7.5" fill={COLORS[color as keyof typeof COLORS]} />
          </svg>
        </>
      ),
      value: COLORS[color as keyof typeof COLORS],
    }));

    const audioSubmenu = showAudioSubmenu
      ? {
          id: AUDIO_MENU_ID,
          label: t('ui.menu.item.audio'),
          value: chosenAudioTrackId || '',
          defaultValue: t('ui.menu.item.quality.auto'),
          showHeader: true,
          options: audioMenuOptions,
        }
      : undefined;
    const subtitlesSubmenu: MenuOptions | undefined = showSubtitlesSubmenu
      ? {
          id: SUBTITLES_MENU_ID,
          label: t('ui.menu.item.subtitles'),
          value: subtitlesEnabled ? chosenSubtitleId : 'off',
          showHeader: true,
          options: [
            {
              id: 'subtitles-options',
              label: t('ui.subtitles.style-options.label'),
              headerSubmenu: true,
              options: [
                resetSubtitleStyleOptions &&
                  ({
                    id: 'reset-subtitle-options',
                    headerSubmenu: true,
                    label: t('ui.subtitles.style-options.reset-options.label'),
                    onClick: (e) => {
                      resetSubtitleStyleOptions();
                      e.stopPropagation();
                    },
                  } as MenuOptions),
                {
                  id: 'subtitle-font',
                  label: t('ui.subtitles.style-options.font-family.label'),
                  value: subtitleStyleOptions?.fontFamily,
                  options: [
                    {
                      id: 'Montserrat',
                      label: t('ui.subtitles.style-options.font-family.default.label'),
                      value: 'Montserrat',
                    },
                    {
                      id: '"Courier New", Courier, "Nimbus Mono L", "Cutive Mono", monospace',
                      label: t('ui.subtitles.style-options.font-family.monospaced-serif.label'),
                      value: '"Courier New", Courier, "Nimbus Mono L", "Cutive Mono", monospace',
                    },
                    {
                      id: '"Times New Roman", Times, Georgia, Cambria, "PT Serif Caption", serif',
                      label: t('ui.subtitles.style-options.font-family.proportional-serif.label'),
                      value: '"Times New Roman", Times, Georgia, Cambria, "PT Serif Caption", serif',
                    },
                    {
                      id: '"Deja Vu Sans Mono", "Lucida Console", Monaco, Consolas, "PT Mono", monospace',
                      label: t('ui.subtitles.style-options.font-family.monospaced-sans-serif.label'),
                      value: '"Deja Vu Sans Mono", "Lucida Console", Monaco, Consolas, "PT Mono", monospace',
                    },
                    {
                      id: 'Roboto, Arial, Helvetica, Verdana, "PT Sans Caption", sans-serif',
                      label: t('ui.subtitles.style-options.font-family.proportional-sans-serif.label'),
                      value: 'Roboto, Arial, Helvetica, Verdana, "PT Sans Caption", sans-serif',
                    },
                    {
                      id: 'Arial, Helvetica, Verdana, "Marcellus SC", sans-serif|small-caps',
                      label: t('ui.subtitles.style-options.font-family.small-capitals.label'),
                      value: 'Arial, Helvetica, Verdana, "Marcellus SC", sans-serif|small-caps',
                    },
                  ],
                },
                {
                  id: 'subtitle-text-size',
                  label: t('ui.subtitles.style-options.font-size.label'),
                  value: subtitleStyleOptions?.fontSize,
                  options: [
                    { id: '3', label: '300%', value: '3' },
                    { id: '2', label: '200%', value: '2' },
                    { id: '1.5', label: '150%', value: '1.5' },
                    { id: '1', label: '100%', value: '1' },
                    { id: '0.5', label: '50%', value: '0.5' },
                  ],
                },
                {
                  id: 'subtitle-text-color',
                  label: t('ui.subtitles.style-options.font-color.label'),
                  value: subtitleStyleOptions?.fontColor,
                  options: colorOptions,
                },
                {
                  id: 'subtitle-background-color',
                  label: t('ui.subtitles.style-options.background-color.label'),
                  value: subtitleStyleOptions?.backgroundColor,
                  options: colorOptions,
                },
                {
                  id: 'subtitle-transparency',
                  label: t('ui.subtitles.style-options.background-transparency.label'),
                  value: subtitleStyleOptions?.backgroundTransparency,
                  options: [
                    { id: '0%', label: '0 %', value: '0%' },
                    { id: '25%', label: '25 %', value: '25%' },
                    { id: '50%', label: '50 %', value: '50%' },
                    { id: '75%', label: '75 %', value: '75%' },
                    { id: '100%', label: '100 %', value: '100%' },
                  ],
                },
              ].filter((item) => !!item),
            },
            ...subtitlesMenuOptions,
          ],
        }
      : undefined;
    const submenus = [audioSubmenu, subtitlesSubmenu].filter((item) => !!item);

    const menu =
      submenus.length === 1
        ? submenus[0]
        : { id: LANGUAGE_MENU_ID, label: t('ui.menu.item.language'), options: submenus };

    return menu;
  }, [
    audioTracks?.length,
    chosenAudioTrackId,
    chosenSubtitleId,
    menuItems,
    resetSubtitleStyleOptions,
    subtitleStyleOptions?.backgroundColor,
    subtitleStyleOptions?.backgroundTransparency,
    subtitleStyleOptions?.fontColor,
    subtitleStyleOptions?.fontFamily,
    subtitleStyleOptions?.fontSize,
    subtitles?.length,
    subtitlesEnabled,
    t,
  ]);

  const onChooseItem = useCallback(
    (currentScreen: string, itemId: string) => {
      switch (currentScreen) {
        case AUDIO_MENU_ID:
          onSetAudioTrack?.(itemId);
          break;
        case SUBTITLES_MENU_ID:
          onSetSubtitle?.(itemId);
          break;
        case 'subtitle-font':
          setSubtitleStyleOptions?.({ ...subtitleStyleOptions, fontFamily: itemId } as SubtitleStyleOptions);
          break;
        case 'subtitle-text-size':
          setSubtitleStyleOptions?.({ ...subtitleStyleOptions, fontSize: itemId } as SubtitleStyleOptions);
          break;
        case 'subtitle-text-color':
          setSubtitleStyleOptions?.({ ...subtitleStyleOptions, fontColor: itemId } as SubtitleStyleOptions);
          break;
        case 'subtitle-background-color':
          setSubtitleStyleOptions?.({ ...subtitleStyleOptions, backgroundColor: itemId } as SubtitleStyleOptions);
          break;
        case 'subtitle-transparency':
          setSubtitleStyleOptions?.({
            ...subtitleStyleOptions,
            backgroundTransparency: itemId,
          } as SubtitleStyleOptions);
          break;
      }

      if (currentScreen === AUDIO_MENU_ID) {
        onSetAudioTrack?.(itemId);
      } else if (currentScreen === SUBTITLES_MENU_ID) {
        onSetSubtitle?.(itemId);
      }
    },
    [onSetAudioTrack, onSetSubtitle, setSubtitleStyleOptions, subtitleStyleOptions]
  );

  return (
    <MenuWrapper
      isOpen={isOpen}
      onClose={onClose}
      menuOptions={menuOptions}
      onChooseItem={onChooseItem}
      buttonName="language-menu-button"
    />
  );
}
